import { Component, OnInit, OnDestroy } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { TagService } from '../../services/tag.service';
import { ReferenzeService } from '../../services/referenze.service';
import { TagModel } from '../../core/models/api/tag.model';
import { ReferenzaFirestore } from '../../core/models/firestore/referenza.model';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { UserRoleService } from '../../services/user-role.service';
import { SearchShareService } from '../../services/search-share.service';

@Component({
  selector: 'app-tags',
  standalone: false,
  templateUrl: './tags.component.html',
  styleUrl: './tags.component.scss'
})
export class TagsComponent implements OnInit, OnDestroy {
  tags: TagModel[] = [];
  filteredTags: TagModel[] = [];
  referenze: ReferenzaFirestore[] = [];
  loading = false;
  selectedCategory: string | null = null;
  searchCodice: string = '';
  categories: Array<{ code: string; name: string; count: number }> = [];

  // Dialog creazione tag
  showCreateTagDialog = false;
  createTagInitialCategory: string | null = null;

  private tagsSub?: Subscription;
  private referenzeSub?: Subscription;

  constructor(
    private tagService: TagService,
    private referenzeService: ReferenzeService,
    private logger: NGXLogger,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private userRoleService: UserRoleService,
    private route: ActivatedRoute,
    private router: Router,
    private searchShareService: SearchShareService
  ) {}

  /**
   * Verifica se l'utente può modificare (non è VIEWER)
   */
  canModify(): boolean {
    return this.userRoleService.canModify();
  }

  /**
   * Verifica se l'utente è VIEWER
   */
  isViewer(): boolean {
    return this.userRoleService.isViewer();
  }

  ngOnInit(): void {
    this.loadTags();
    this.loadReferenze();

    // Auto-apertura dialog creazione da querystring
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      try {
        if (params && params['create'] === 'true') {
          this.createTagInitialCategory = params['category'] || null;
          this.showCreateTagDialog = true;
        }
      } catch (err) {
        this.logger.error('[TAGS] Errore gestione query params per creazione tag:', err);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.tagsSub) {
      this.tagsSub.unsubscribe();
    }
    if (this.referenzeSub) {
      this.referenzeSub.unsubscribe();
    }
  }

  /**
   * Carica i tag dal backend
   */
  private loadTags(): void {
    this.loading = true;
    this.tagsSub = this.tagService.getTags().subscribe({
      next: tags => {
        this.tags = tags;
        this.extractCategories();
        this.applyFilter();
        this.loading = false;
        this.logger.info('[TAGS] Tag caricati:', tags.length);
      },
      error: err => {
        this.logger.error('[TAGS] Errore nel caricamento dei tag:', err);
        this.loading = false;
      }
    });
  }

  /**
   * Estrae le categorie uniche dai tag
   */
  private extractCategories(): void {
    const categoryMap = new Map<string, { name: string; count: number }>();
    this.logger.debug('[TAGS] Estrazione categorie:', this.tags.length);
    this.tags.forEach(tag => {
      const code = tag.categoriaTagCodice;
      const name = tag.categoriaTagNome;
      this.logger.debug('[TAGS] Categoria:', code, name);

      if (categoryMap.has(code)) {
        categoryMap.get(code)!.count++;
      } else {
        categoryMap.set(code, { name, count: 1 });
      }
    });

    // Ordine fisso delle categorie mostrato nel filtro
    const desiredOrder = ['FASI', 'TEMATICHE', 'TIPOLOGIA', 'PRESTAZIONI', 'TIPO_STRUTTURE'];

    this.categories = Array.from(categoryMap.entries())
      .map(([code, data]) => ({
        code,
        name: data.name,
        count: data.count
      }))
      // Considera solo le 5 categorie previste e ordinale in modo esplicito
      .filter(cat => desiredOrder.includes(cat.code))
      .sort((a, b) => desiredOrder.indexOf(a.code) - desiredOrder.indexOf(b.code));
  }

  /**
   * Applica il filtro per categoria
   */
  private applyFilter(): void {
    let result = this.tags;

    if (this.selectedCategory) {
      result = result.filter(tag => tag.categoriaTagCodice === this.selectedCategory);
    }

    const term = this.searchCodice.trim().toLowerCase();
    if (term) {
      result = result.filter(tag => tag.codice?.toLowerCase().includes(term));
    }

    this.filteredTags = result;
  }

  /**
   * Seleziona una categoria per filtrare
   */
  selectCategory(categoryCode: string): void {
    if (this.selectedCategory === categoryCode) {
      // Se clicchi sulla stessa categoria, rimuovi il filtro
      this.selectedCategory = null;
    } else {
      this.selectedCategory = categoryCode;
    }
    this.applyFilter();
  }

  /**
   * Rimuove tutti i filtri
   */
  clearFilter(): void {
    this.selectedCategory = null;
    this.searchCodice = '';
    this.applyFilter();
  }

  /**
   * Verifica se una categoria è selezionata
   */
  isCategorySelected(categoryCode: string): boolean {
    return this.selectedCategory === categoryCode;
  }

  /**
   * Aggiorna il filtro per codice tag (ricerca testuale progressiva)
   */
  onSearchCodiceChange(value: string): void {
    this.searchCodice = value || '';
    this.applyFilter();
  }

  /**
   * Ottiene il colore del badge in base alla categoria del tag
   */
  getCategoryColor(categoryCode: string): string {
    if (!categoryCode) return '#6b7280'; // grigio di default

    // Mappa colori per categoria (nuova palette, 5 categorie)
    const categoryColors: Record<string, string> = {
      'FASI': '#0ea5e9',           // azzurro
      'TEMATICHE': '#22c55e',      // verde
      'TIPOLOGIA': '#8b5cf6',      // viola
      'PRESTAZIONI': '#f97316',    // arancione
      'TIPO_STRUTTURE': '#ec4899'  // rosa
    };

    return categoryColors[categoryCode] || '#6b7280'; // grigio di default
  }

  /**
   * Ottiene il colore del testo (bianco o nero) in base alla luminosità del colore di sfondo
   */
  getCategoryTextColor(categoryCode: string): string {
    const bgColor = this.getCategoryColor(categoryCode);
    // Converti hex a RGB
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calcola la luminosità relativa
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Se la luminosità è alta, usa testo nero, altrimenti bianco
    return luminance > 0.5 ? '#1f2937' : '#ffffff';
  }

  /**
   * Ottiene il nome della categoria dal codice
   */
  getCategoryName(categoryCode: string): string {
    // Nomi uniformi per le 5 categorie principali
    const nameMap: Record<string, string> = {
      'FASI': 'Fasi',
      'TEMATICHE': 'Tematiche',
      'TIPOLOGIA': 'Tipologia',
      'PRESTAZIONI': 'Prestazioni',
      'TIPO_STRUTTURE': 'Tipo strutture'
    };

    const normalized = nameMap[categoryCode];
    if (normalized) {
      return normalized;
    }

    const category = this.categories.find(c => c.code === categoryCode);
    return category ? category.name : categoryCode;
  }

  /**
   * Carica le referenze da Firestore
   */
  private loadReferenze(): void {
    this.referenzeSub = this.referenzeService.listenReferenze().subscribe({
      next: referenze => {
        this.referenze = referenze;
        this.logger.info('[TAGS] Referenze caricate:', referenze.length);
        this.logger.info('[TAGS] Referenze:', referenze);
      },
      error: err => {
        this.logger.error('[TAGS] Errore nel caricamento delle referenze:', err);
      }
    });
  }

  /**
   * Normalizza una stringa rimuovendo accenti e caratteri speciali per il matching
   * Converte spazi in underscore e tutto in maiuscolo per matchare il formato delle referenze
   */
  private normalizeString(str: string): string {
    if (!str) return '';
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Rimuove i diacritici (accenti)
      .replace(/[''""]/g, "'") // Normalizza apostrofi
      .replace(/\s+/g, '_') // Converte spazi in underscore (come nel formato referenze)
      .trim() // Rimuove spazi iniziali/finali
      .toUpperCase();
  }

  /**
   * Converte il codice del tag dal formato DB (minuscolo con spazi)
   * al formato referenze (maiuscolo con underscore)
   * Es: "indagini archeologiche" -> "INDAGINI_ARCHEOLOGICHE"
   */
  private convertCodiceToReferenzeFormat(codice: string): string {
    if (!codice) return '';
    return codice
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Rimuove accenti
      .replace(/[''""]/g, "'") // Normalizza apostrofi
      .replace(/\s+/g, '_') // Converte spazi in underscore
      .trim()
      .toUpperCase();
  }

  /**
   * Conta il numero di referenze che contengono questo tag
   */
  getReferenzeCount(tag: TagModel): number {
    if (!this.referenze || this.referenze.length === 0) {
      return 0;
    }

    // Converte il codice dal formato DB al formato referenze (maiuscolo + underscore)
    const codiceFormatoReferenze = this.convertCodiceToReferenzeFormat(tag.codice);
    const expectedTag = `${tag.categoriaTagCodice}#${codiceFormatoReferenze}`;
    const normalizedExpected = this.normalizeString(expectedTag);

    // Debug per il tag specifico
    const isDebugTag = tag.codice && (
      tag.codice.toLowerCase().includes('verifica') &&
      tag.codice.toLowerCase().includes('vulnerabilita')
    );

    if (isDebugTag) {
      this.logger.debug('[TAGS] Debug tag "Verifica Vulnerabilità Sismica":', {
        codice: tag.codice,
        categoriaTagCodice: tag.categoriaTagCodice,
        expectedTag,
        normalizedExpected,
        totalReferenze: this.referenze.length
      });
    }

    const count = this.referenze.filter(ref => {
      if (!ref.tag || ref.tag.length === 0) {
        return false;
      }

      const found = ref.tag.some(refTag => {
        const normalizedRefTag = this.normalizeString(refTag);
        const matches = normalizedRefTag === normalizedExpected;

        // Debug per il tag specifico - mostra tutti i tag delle referenze che contengono parole chiave
        if (isDebugTag) {
          const refTagLower = refTag.toLowerCase();
          const codiceLower = tag.codice.toLowerCase();
          if (refTagLower.includes('verifica') || refTagLower.includes('vulnerabilita') ||
              codiceLower.includes('verifica') || codiceLower.includes('vulnerabilita')) {
            this.logger.debug('[TAGS] Confronto tag referenza:', {
              refTag,
              normalizedRefTag,
              expectedTag,
              normalizedExpected,
              matches,
              referenzaTitolo: ref.titoloBreve
            });
          }
        }

        return matches;
      });

      return found;
    }).length;

    // Debug per il tag specifico
    if (isDebugTag) {
      this.logger.debug('[TAGS] Conteggio referenze per tag "Verifica Vulnerabilità Sismica":', count);

      // Mostra anche alcuni esempi di tag dalle referenze per debug
      const sampleTags = this.referenze
        .filter(ref => ref.tag && ref.tag.length > 0)
        .flatMap(ref => ref.tag)
        .filter(t => this.normalizeString(t).includes('VERIFICA') || this.normalizeString(t).includes('VULNERABILITA'))
        .slice(0, 5);

      if (sampleTags.length > 0) {
        this.logger.debug('[TAGS] Esempi di tag trovati nelle referenze che contengono "verifica" o "vulnerabilita":', sampleTags);
      }
    }

    return count;
  }

  /**
   * Apre il dialog per creare un nuovo tag
   */
  openCreateTagDialog(): void {
    this.showCreateTagDialog = true;
    this.createTagInitialCategory = this.selectedCategory;
  }

  /**
   * Chiude il dialog di creazione tag
   */
  closeCreateTagDialog(): void {
    this.showCreateTagDialog = false;
    this.createTagInitialCategory = null;
  }

  onTagCreated(): void {
    this.loadTags();
  }

  /**
   * Conferma l'eliminazione di un tag
   */
  confirmDeleteTag(tag: TagModel): void {
    const categoryColor = this.getCategoryColor(tag.categoriaTagCodice);
    const categoryTextColor = this.getCategoryTextColor(tag.categoriaTagCodice);

    this.confirmationService.confirm({
      message: `
        <div class="delete-confirm-message">
          <p class="confirm-text">Sei sicuro di voler eliminare questo tag?</p>
          <div class="tag-details">
            <div class="detail-row">
              <span class="detail-label">Categoria:</span>
              <span class="detail-value category-badge-inline"
                    style="background-color: ${categoryColor}; color: ${categoryTextColor};">
                ${tag.categoriaTagNome.toUpperCase()}
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Codice:</span>
              <span class="detail-value code-value">${tag.codice}</span>
            </div>
          </div>
          <p class="warning-text">Questa azione non può essere annullata.</p>
        </div>
      `,
      header: 'Conferma Eliminazione',
      icon: 'none',
      acceptLabel: 'Elimina',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.deleteTag(tag);
      }
    });
  }

  /**
   * Elimina un tag
   */
  deleteTag(tag: TagModel): void {
    this.tagService.deleteTag(tag.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Tag eliminato con successo',
          life: 3000
        });
        // Ricarica i tag per aggiornare la lista
        this.loadTags();
      },
      error: (error) => {
        this.logger.error('[TAGS] Errore nell\'eliminazione del tag:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error.error?.message || 'Errore durante l\'eliminazione del tag',
          life: 5000
        });
      }
    });
  }

  /**
   * Naviga alla home con il filtro impostato sul tag selezionato
   */
  navigateToHomeWithTag(tag: TagModel): void {
    const count = this.getReferenzeCount(tag);

    // Non navigare se non ci sono referenze
    if (count === 0) {
      return;
    }

    // Usa il codice originale del tag (non quello convertito)
    // La home confronta con tag.codice, non con il formato referenze
    const codiceTags = tag.codice;

    // Crea i parametri di ricerca con il tag selezionato
    const searchParams = {
      selectedTagsByCategory: {
        [tag.categoriaTagCodice]: [codiceTags]
      }
    };

    // Genera la query string
    const queryString = this.searchShareService.serializeSearchParams(searchParams);

    // Costruisce i query params dall'URL generata
    // Il formato è: tags=CATEGORIA:tag1,tag2
    const urlParams = new URLSearchParams(queryString);
    const queryParams: any = {};
    urlParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    // Naviga alla home con i parametri
    this.router.navigate(['/'], { queryParams });

    this.logger.debug('[TAGS] Navigazione alla home con tag:', {
      categoria: tag.categoriaTagCodice,
      codice: codiceTags,
      queryParams
    });
  }
}


